import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { platformApi } from '@/lib/api';
import { usePlatformAuth } from '@/contexts/PlatformAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableIndexCell, TableIndexHead } from '@/components/ui/table-index';
import { rowSerial } from '@/lib/row-serial';
import { Pagination } from '@/components/ui/pagination';
import { useClientPagination } from '@/hooks/useClientPagination';
import { PageLoading } from '@/components/ui/loading';
import { formatDate } from '@/lib/utils';
import { Eye, EyeOff, Info } from 'lucide-react';

interface RequirementRow {
  id: string;
  requirement_number: string;
  projectName?: string;
  status: string;
  grandTotal?: number;
  createdAt?: string;
  created_at?: string;
}

export default function SupportDashboardPage() {
  const { support, exitSupport } = usePlatformAuth();

  const { data: requirements, isLoading } = useQuery({
    queryKey: ['support', 'requirements'],
    queryFn: async () => {
      const r = await platformApi.get<{ success: boolean; data: RequirementRow[] }>('/requirements?limit=1000');
      return r.data ?? [];
    },
    enabled: !!support,
  });

  const { data: org } = useQuery({
    queryKey: ['support', 'org'],
    queryFn: async () => {
      if (!support) return null;
      const r = await platformApi.get<{ success: boolean; data: any }>(`/platform/organizations/${support.organizationId}`);
      return r.data;
    },
    enabled: !!support,
  });

  const { page, setPage, pageSize, total, pageItems } = useClientPagination(requirements ?? [], {
    resetKey: support?.organizationId,
  });

  if (!support) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-muted gap-3">
        <Info size={40} className="opacity-40" />
        <p>You are not in support mode.</p>
        <Link to="/super-admin/organizations"><Button variant="outline" size="sm">Go to Organizations</Button></Link>
      </div>
    );
  }

  if (isLoading) return <PageLoading />;

  const statusBadge = (status: string) => {
    if (status === 'APPROVED') return <Badge variant="approved">{status}</Badge>;
    if (status.includes('REJECTED')) return <Badge variant="rejected">{status}</Badge>;
    if (status === 'DRAFT') return <Badge variant="draft">{status}</Badge>;
    return <Badge variant="submitted">{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-heading font-bold text-primary">Support Mode</h1>
          <p className="text-sm text-secondary mt-1">
            Viewing <span className="font-medium">{support.organizationName || support.organizationCode}</span>
          </p>
        </div>
        <Button variant="outline" className="gap-2 flex-shrink-0" onClick={exitSupport}><EyeOff size={14} /> Exit Support Mode</Button>
      </div>

      <Card>
        <CardContent className="p-5 grid gap-4 sm:grid-cols-3">
          <div><span className="text-xs text-secondary uppercase">Organization</span><p className="font-medium">{org?.name || support.organizationName}</p></div>
          <div><span className="text-xs text-secondary uppercase">Code</span><p className="font-mono font-medium">{org?.organization_code || support.organizationCode}</p></div>
          <div><span className="text-xs text-secondary uppercase">Reason</span><p className="text-sm">{support.reason}</p></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Eye size={16} /> Recent Requirements (read-only)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableIndexHead /><TableHead>Requirement</TableHead><TableHead>Project</TableHead><TableHead>Status</TableHead><TableHead>Grand Total</TableHead><TableHead>Created</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((r, index) => (
                <TableRow key={r.id}>
                  <TableIndexCell value={rowSerial(index, page, pageSize)} />
                  <TableCell className="font-mono text-sm">{r.requirement_number}</TableCell>
                  <TableCell className="text-sm">{r.projectName || '—'}</TableCell>
                  <TableCell>{statusBadge(r.status)}</TableCell>
                  <TableCell className="text-sm font-medium">{r.grandTotal != null ? `₹${Number(r.grandTotal).toLocaleString('en-IN')}` : '—'}</TableCell>
                  <TableCell className="text-sm text-muted">{formatDate(r.created_at || r.createdAt)}</TableCell>
                </TableRow>
              ))}
              {(requirements ?? []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted">No requirements in this organization</TableCell></TableRow>}
            </TableBody>
          </Table>
          {(requirements ?? []).length > 0 && <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />}
        </CardContent>
      </Card>
    </div>
  );
}
